import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import SearchPage from './pages/SearchPage';
import ReportPage from './pages/ReportPage';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import AccountsPage from './pages/AccountsPage';
import ClientsPage from './pages/ClientsPage';
import AlertsPage from './pages/AlertsPage';
import AcceptInvitePage from './pages/AcceptInvitePage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#102B3D',
      dark: '#0C2230',
      light: '#EAF1F5',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#10A4C2',
      dark: '#0D8DA7',
      light: '#E8F8FB',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1F2937',
      secondary: '#6B7280',
    },
    divider: '#D7E1E8',
    info: {
      main: '#10A4C2',
    },
    success: {
      main: '#059669',
    },
    error: {
      main: '#DC2626',
    },
    warning: {
      main: '#D97706',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      color: '#102B3D',
    },
    h5: {
      fontWeight: 700,
      color: '#102B3D',
    },
    h6: {
      fontWeight: 600,
      color: '#102B3D',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#102B3D',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderColor: '#D7E1E8',
        },
      },
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: '#102B3D',
            backgroundColor: '#EAF1F5',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#10A4C2',
          '&:hover': {
            color: '#0D8DA7',
          },
        },
      },
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { access, isLoading } = useAuth();
  if (isLoading) return null;
  if (!access) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

// Admin pages: firm users who type the URL go home instead of seeing an
// empty admin page (the API refuses them either way).
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { access, isLoading, user } = useAuth();
  if (isLoading) return null;
  if (!access) return <Navigate to="/login" replace />;
  if (!user) return null;
  if (!user.is_superuser) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
      <Route path="/" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/reports/:uuid" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/accounts" element={<AdminRoute><AccountsPage /></AdminRoute>} />
      <Route path="/clients" element={<AdminRoute><ClientsPage /></AdminRoute>} />
      <Route path="/alerts" element={<AdminRoute><AlertsPage /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
